import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useMe } from '../lib/queries.js';
import { useStore } from '../store/useStore.js';

const errorLabel: Record<string, string> = {
  invalid_name: 'Nome inválido (1–120 caracteres).',
  invalid_payload: 'Dados inválidos.',
  invalid_current_password: 'Senha atual incorreta.',
  weak_password: 'Senha nova precisa ter ao menos 8 caracteres.',
  unauthenticated: 'Sessão expirada. Faça login novamente.',
  user_not_found: 'Usuário não encontrado.',
};

export function ProfilePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me, isLoading } = useMe();
  const setAuth = useStore((s) => s.setAuth);

  const [name, setName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [avatarBump, setAvatarBump] = useState(0); // cache-bust após upload

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarMsg(null);
    if (file.size > 2 * 1024 * 1024) {
      setAvatarMsg({ tone: 'err', text: 'Imagem muito grande (máx. 2 MB).' });
      return;
    }
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/auth/avatar', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) throw new Error('upload_failed');
      const body = await res.json();
      qc.setQueryData(['me'], (prev: any) => ({ ...(prev ?? {}), image: body.image }));
      setAvatarBump(Date.now());
      setAvatarMsg({ tone: 'ok', text: 'Foto atualizada.' });
    } catch {
      setAvatarMsg({ tone: 'err', text: 'Falha ao enviar a foto.' });
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  }

  useEffect(() => {
    if (me?.name) setName(me.name);
  }, [me?.name]);

  async function submitName(e: React.FormEvent) {
    e.preventDefault();
    if (nameSaving) return;
    setNameMsg(null);
    setNameSaving(true);
    try {
      const updated = await api<{ id: string; email: string; name: string }>('/auth/me', {
        method: 'PATCH',
        body: { name },
      });
      qc.setQueryData(['me'], (prev: any) => ({ ...(prev ?? {}), name: updated.name }));
      setAuth({ id: updated.id, email: updated.email, name: updated.name, isAdmin: me?.admin !== null });
      setNameMsg({ tone: 'ok', text: 'Nome atualizado.' });
    } catch (err: any) {
      const code = err?.body?.error ?? 'unknown';
      setNameMsg({ tone: 'err', text: errorLabel[code] ?? 'Falha ao salvar.' });
    } finally {
      setNameSaving(false);
    }
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (pwSaving) return;
    setPwMsg(null);
    if (newPw.length < 8) {
      setPwMsg({ tone: 'err', text: errorLabel.weak_password ?? 'Senha fraca.' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ tone: 'err', text: 'Confirmação não confere.' });
      return;
    }
    setPwSaving(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: { newPassword: newPw },
      });
      setPwMsg({ tone: 'ok', text: 'Senha alterada.' });
      setNewPw('');
      setConfirmPw('');
    } catch (err: any) {
      const code = err?.body?.error ?? 'unknown';
      setPwMsg({ tone: 'err', text: errorLabel[code] ?? 'Falha ao alterar senha.' });
    } finally {
      setPwSaving(false);
    }
  }

  if (isLoading) {
    return <div style={{ padding: 32, color: 'var(--muted)' }}>Carregando…</div>;
  }
  if (!me) {
    return <div style={{ padding: 32, color: 'var(--muted)' }}>Não autenticado.</div>;
  }

  return (
    <main style={{
      minHeight: '100dvh', background: 'var(--canvas-bg)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '48px 24px',
    }}>
      <div style={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{
            fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em',
            background: 'linear-gradient(135deg, var(--neon), var(--violet))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>Perfil</h1>
          <button
            onClick={() => navigate({ to: '/' })}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 8, padding: '6px 12px', color: 'var(--text-dim)',
              fontSize: 12, cursor: 'pointer',
            }}
          >Voltar</button>
        </header>

        <section style={{ ...panelStyle, flexDirection: 'row', alignItems: 'center', gap: 18 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {(me as any).image ? (
              <img
                src={`/api/auth/avatar/${me.id}?v=${avatarBump}`}
                alt="Foto de perfil"
                style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-hover)' }}
              />
            ) : (
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--neon), var(--violet))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, fontWeight: 700, color: '#02131a',
              }}>
                {(me.name || me.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div style={metaRowStyle}><span style={metaLabel}>Email</span><span style={{ color: 'var(--text)' }}>{me.email}</span></div>
            {me.admin && <div style={metaRowStyle}><span style={metaLabel}>Função</span><span style={{ color: 'var(--neon)' }}>{me.admin}</span></div>}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              style={{
                alignSelf: 'flex-start', background: 'rgba(0,245,255,0.08)',
                border: '1px solid var(--border)', borderRadius: 8,
                padding: '6px 12px', color: 'var(--neon)', fontSize: 12,
                fontWeight: 600, cursor: avatarUploading ? 'wait' : 'pointer',
              }}
            >
              {avatarUploading ? 'Enviando…' : (me as any).image ? 'Trocar foto' : 'Adicionar foto'}
            </button>
            {avatarMsg && <p style={msgStyle(avatarMsg.tone)}>{avatarMsg.text}</p>}
          </div>
        </section>

        <form onSubmit={submitName} style={panelStyle}>
          <h2 style={panelTitleStyle}>Nome</h2>
          <label style={fieldLabelStyle}>
            <span>Nome de exibição</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              autoComplete="name"
              style={inputStyle}
              maxLength={120}
            />
          </label>
          {nameMsg && <p style={msgStyle(nameMsg.tone)}>{nameMsg.text}</p>}
          <button type="submit" disabled={nameSaving || !name.trim() || name.trim() === me.name} style={primaryBtnStyle(nameSaving || !name.trim())}>
            {nameSaving ? 'Salvando…' : 'Salvar nome'}
          </button>
        </form>

        <form onSubmit={submitPassword} style={panelStyle}>
          <h2 style={panelTitleStyle}>Senha</h2>
          <label style={fieldLabelStyle}>
            <span>Nova senha</span>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
              minLength={8}
            />
          </label>
          <label style={fieldLabelStyle}>
            <span>Confirmar nova senha</span>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
              style={inputStyle}
              minLength={8}
            />
          </label>
          {pwMsg && <p style={msgStyle(pwMsg.tone)}>{pwMsg.text}</p>}
          <button
            type="submit"
            disabled={pwSaving || !newPw || !confirmPw}
            style={primaryBtnStyle(pwSaving || !newPw || !confirmPw)}
          >
            {pwSaving ? 'Alterando…' : 'Alterar senha'}
          </button>
        </form>
      </div>
    </main>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'rgba(8, 14, 22, 0.7)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 14, padding: 20,
  display: 'flex', flexDirection: 'column', gap: 14,
  backdropFilter: 'blur(18px)',
};

const panelTitleStyle: React.CSSProperties = {
  fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em',
  color: 'var(--text)', margin: 0,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
  fontSize: 12, color: 'var(--text-dim)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', height: 38, padding: '0 12px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8, color: 'var(--text)',
  fontSize: 14, fontFamily: 'inherit', outline: 'none',
};

const metaRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  fontSize: 13,
};

const metaLabel: React.CSSProperties = { color: 'var(--muted)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' };

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    alignSelf: 'flex-start',
    background: disabled ? 'rgba(0,245,255,0.18)' : 'var(--neon)',
    color: disabled ? 'rgba(2,19,26,0.7)' : '#02131a',
    border: 'none', borderRadius: 9,
    padding: '10px 18px', fontWeight: 600, fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 0 16px rgba(0,245,255,0.32)',
    transition: 'transform .15s, box-shadow .15s',
  };
}

function msgStyle(tone: 'ok' | 'err'): React.CSSProperties {
  return {
    margin: 0, fontSize: 12,
    color: tone === 'ok' ? '#4ade80' : '#f87171',
  };
}
